import {
    BigNumberish,
    InnerSimpleV0Transaction,
    LIQUIDITY_STATE_LAYOUT_V4,
    Liquidity,
    LiquidityPoolKeysV4,
    TOKEN_PROGRAM_ID,
    Token,
    TokenAmount,
    TxVersion,
    buildSimpleTransaction,
    MAINNET_PROGRAM_ID,
    InnerSimpleTransaction,
    CacheLTA,
    Percent,
} from '@raydium-io/raydium-sdk';
import {
    AddressLookupTableAccount,
    Connection,
    Keypair,
    PublicKey,
    Signer,
    Transaction,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';

import { useSplToken } from './useSplToken';
import { useJito } from './useJito';
import deployAmmPoolData from '../interfaces/deploy-amm-pool-data';
import { encode } from 'bs58';

export const useRaydium = () => {
    const { getTokenDecimals, getWalletTokenAccounts } = useSplToken();
    const { getTipInstructions, sendBundle } = useJito();

    async function getMinAmountOut(
        connection: Connection,
        amountIn: number,
        poolKeys: LiquidityPoolKeysV4,
        mintIn: PublicKey,
        mintOut: PublicKey,
        slippage: Percent,
    ) {
        try {
            let decimalsIn, decimalsOut: number;

            if (mintIn.equals(poolKeys.baseMint)) {
                decimalsIn = poolKeys.baseDecimals;
                decimalsOut = poolKeys.quoteDecimals;
            } else {
                decimalsIn = poolKeys.quoteDecimals;
                decimalsOut = poolKeys.baseDecimals;
            }

            const tokenIn = new Token(TOKEN_PROGRAM_ID, mintIn, decimalsIn);
            const tokenInAmount = new TokenAmount(tokenIn, amountIn);

            const currencyOut = new Token(TOKEN_PROGRAM_ID, mintOut, decimalsOut);

            const { minAmountOut } = Liquidity.computeAmountOut({
                poolKeys: poolKeys,
                poolInfo: await Liquidity.fetchInfo({ connection, poolKeys }),
                amountIn: tokenInAmount,
                currencyOut: currencyOut,
                slippage: slippage,
            });
            return minAmountOut;
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    // deprecated
    // async function getMinAmountOutV2(
    //     connection: Connection,
    //     amountIn: number,
    //     poolKeys: LiquidityPoolKeysV4,
    //     mintIn: PublicKey,
    //     mintOut: PublicKey,
    //     decimalsIn: number,
    //     decimalsOut: number,
    //     slippage: Percent,
    // ) {
    //     const tokenIn = new Token(TOKEN_PROGRAM_ID, mintIn, decimalsIn);
    //     const tokenInAmount = new TokenAmount(tokenIn, amountIn);
    //
    //     const currencyOut = new Token(TOKEN_PROGRAM_ID, mintOut, decimalsOut);
    //
    //     const { minAmountOut } = Liquidity.computeAmountOut({
    //         poolKeys: poolKeys,
    //         poolInfo: await Liquidity.fetchInfo({ connection, poolKeys }),
    //         amountIn: tokenInAmount,
    //         currencyOut: currencyOut,
    //         slippage: slippage,
    //     });
    //
    //     return minAmountOut;
    // }

    async function swap(
        connection: Connection,
        jitoTipPayer: Keypair,
        walletFrom: Keypair,
        poolKeys: LiquidityPoolKeysV4,
        amountIn: BigNumberish,
        amountOut: BigNumberish,
        tokenInKey: PublicKey,
        tokenOutKey: PublicKey,
        priorityGas: number,
        jitoTipLamports: number,
        logMessage: (message: string, type?: 'error' | 'success') => void,
        confirm: boolean = false,
        useJito: boolean = true,
    ) {
        // try {
        const response = await getSwapInstructions(
            connection,
            walletFrom,
            walletFrom,
            poolKeys,
            amountIn,
            amountOut,
            tokenInKey,
            tokenOutKey,
            'in',
            priorityGas,
        );
        const transaction = (await buildSimpleTransaction({
            connection: connection,
            makeTxVersion: TxVersion.V0,
            payer: walletFrom.publicKey, //tx fee payer
            innerTransactions: response,
        })) as VersionedTransaction[];
        transaction[0].sign([walletFrom]);

        if (useJito) {
            const bundleId = await sendBundle(connection, [transaction[0]], jitoTipPayer, jitoTipLamports, false);
            logMessage(`Swap bundle sent for ${walletFrom.publicKey.toBase58()}, bundleId: ${bundleId}`);
        } else {
            await connection.sendTransaction(transaction[0], { skipPreflight: true });
        }
        if (confirm) {
            const { lastValidBlockHeight, blockhash } = await connection.getLatestBlockhash();
            const signature = encode(transaction[0].signatures[0]);
            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature }, 'processed');
        }

        return;
        // } catch (error) {
        //     console.log(error);
        //     throw error;
        // }
    }

    async function getSwapInstructions(
        connection: Connection,
        payer: Keypair, //swap fee payer
        receiver: Keypair,
        poolKeys: LiquidityPoolKeysV4,
        amountIn: BigNumberish,
        amountOut: BigNumberish,
        mintIn: PublicKey,
        mintOut: PublicKey,
        fixedSide: 'in' | 'out',
        priorityGas: number,
    ) {
        //try {
        const walletTokenAccounts = await getWalletTokenAccounts(connection, receiver.publicKey);

        //currency means SOLANA
        const decimalsIn = await getTokenDecimals(connection, mintIn);
        const tokenIn = new Token(TOKEN_PROGRAM_ID, mintIn, decimalsIn);
        const tokenInAmount = new TokenAmount(tokenIn, amountIn);

        const decimalsOut = await getTokenDecimals(connection, mintOut);
        const tokenOut = new Token(TOKEN_PROGRAM_ID, mintOut, decimalsOut);
        const tokenOutAmount = new TokenAmount(tokenOut, amountOut);

        const response = await Liquidity.makeSwapInstructionSimple({
            connection: connection,
            poolKeys: poolKeys,
            userKeys: {
                tokenAccounts: walletTokenAccounts,
                owner: receiver.publicKey,
                payer: payer.publicKey,
            },
            amountIn: tokenInAmount,
            amountOut: tokenOutAmount,
            fixedSide: fixedSide,
            makeTxVersion: TxVersion.V0,
            computeBudgetConfig: {
                microLamports: priorityGas,
            },
        });

        return response.innerTransactions;
        // } catch (error) {
        //     console.log('Get swap instructions: ', error);
        //     throw error;
        // }
    }

    async function getPoolByMints(connections: Connection[], mint1: PublicKey, mint2: PublicKey) {
        let market;
        try {
            market = (await fetchMarketAccountsByMintsWithMultipleConnections(connections, new PublicKey(mint1), new PublicKey(mint2)))[0].id;
        } catch {
            market = (await fetchMarketAccountsByMintsWithMultipleConnections(connections, new PublicKey(mint2), new PublicKey(mint1)))[0].id;
        }
        return market;
    }

    async function fetchMarketAccountsByMints(
        connection: Connection,
        base: PublicKey,
        quote: PublicKey,
        commitment: 'processed' | 'confirmed' | 'singleGossip' | 'single' | 'recent' | 'max' = 'processed',
    ) {
        try {
            const accounts = await connection.getProgramAccounts(MAINNET_PROGRAM_ID.AmmV4, {
                commitment,
                filters: [
                    { dataSize: LIQUIDITY_STATE_LAYOUT_V4.span },
                    {
                        memcmp: {
                            offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf('baseMint'),
                            bytes: base.toBase58(),
                        },
                    },
                    {
                        memcmp: {
                            offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf('quoteMint'),
                            bytes: quote.toBase58(),
                        },
                    },
                ],
            });
            return accounts.map(({ pubkey, account }) => ({
                id: pubkey.toString(),
                ...LIQUIDITY_STATE_LAYOUT_V4.decode(account.data),
            }));
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    async function fetchMarketAccountsByMintsWithMultipleConnections(connections: Connection[], base: PublicKey, quote: PublicKey) {
        const promises = connections.map((connection) => fetchMarketAccountsByMints(connection, base, quote));

        try {
            return await Promise.any(promises);
        } catch (error) {
            throw new Error('All connections failed to fetch market accounts: ' + error);
        }
    }

    async function getdeployAmmPoolInstructions(input: deployAmmPoolData) {
        const initPoolInstructionResponse = await Liquidity.makeCreatePoolV4InstructionV2Simple(input);

        return initPoolInstructionResponse;
    }

    async function buildSimpleTransactionWithInstructionsArray({
        connection,
        makeTxVersion,
        payer,
        innerTransactions,
        recentBlockhash,
        addLookupTableInfo,
        signers,
        jitoTipPayer,
        jitoTipAmount,
    }: {
        makeTxVersion: TxVersion;
        payer: PublicKey;
        connection: Connection;
        innerTransactions: InnerSimpleTransaction[];
        recentBlockhash?: string | undefined;
        addLookupTableInfo?: CacheLTA | undefined;
        signers: (Signer | Keypair)[];
        jitoTipPayer?: Keypair;
        jitoTipAmount?: number;
    }): Promise<VersionedTransaction | Transaction> {
        if (makeTxVersion !== TxVersion.V0 && makeTxVersion !== TxVersion.LEGACY) throw Error(' make tx version args error');

        const _recentBlockhash = recentBlockhash ?? (await connection.getLatestBlockhash()).blockhash;

        const allInstructions: TransactionInstruction[] = [];
        innerTransactions.forEach((item) => {
            allInstructions.push(...item.instructions);
        });

        if (jitoTipPayer && jitoTipAmount) {
            const jitoTipInstruction = await getTipInstructions(jitoTipPayer, jitoTipAmount);
            allInstructions.push(jitoTipInstruction);
        }
        const tx = _makeTransaction({
            makeTxVersion,
            instructions: allInstructions,
            payer,
            recentBlockhash: _recentBlockhash,
            signers: signers,
            lookupTableInfos: Object.values({
                ...(addLookupTableInfo ?? {}),
                ...((innerTransactions[0] as InnerSimpleV0Transaction).lookupTableAddress ?? {}),
            }),
        });

        return tx;
    }

    function _makeTransaction({
        makeTxVersion,
        instructions,
        payer,
        recentBlockhash,
        signers,
        lookupTableInfos,
    }: {
        makeTxVersion: TxVersion;
        instructions: TransactionInstruction[];
        payer: PublicKey;
        recentBlockhash: string;
        signers: (Signer | Keypair)[];
        lookupTableInfos?: AddressLookupTableAccount[];
    }): VersionedTransaction | Transaction {
        if (makeTxVersion === TxVersion.LEGACY) {
            const tx = new Transaction();
            tx.add(...instructions);
            tx.feePayer = payer;
            tx.recentBlockhash = recentBlockhash;
            if (signers.length > 0) tx.sign(...signers);
            return tx;
        } else if (makeTxVersion === TxVersion.V0) {
            const transactionMessage = new TransactionMessage({
                payerKey: payer,
                recentBlockhash,
                instructions,
            });
            const itemV = new VersionedTransaction(transactionMessage.compileToV0Message(lookupTableInfos));
            itemV.sign(signers);
            return itemV;
        } else {
            throw Error(' make tx version check error ');
        }
    }
    return {
        getPoolByMints,
        getMinAmountOut,
        // getMinAmountOutV2,
        swap,
        fetchMarketAccountsByMints,
        getdeployAmmPoolInstructions,
        getSwapInstructions,
        fetchMarketAccountsByMintsWithMultipleConnections,
        buildSimpleTransactionWithInstructionsArray,
    };
};
